import { useState, useEffect } from 'react'
import { Box, Text, Button, Flex, Input } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { User } from 'lucide-react'
import { useLearnerProfile } from '../hooks/useLearnerProfile.js'
import { useAuth } from '../hooks/useAuth.js'
import { fullNameFromUser } from '../lib/userName.js'
import { useAnimatePresence } from '../hooks/useAnimatePresence.js'

interface ProfileEditModalProps {
  show: boolean
  onClose: () => void
}

export function ProfileEditModal({ show, onClose }: ProfileEditModalProps) {
  const { t } = useTranslation('profile')
  const { profile, upsertProfile } = useLearnerProfile()
  const { user } = useAuth()
  const [fullName, setFullName] = useState('')
  const [headline, setHeadline] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (show) {
      // Fall back to the OAuth identity name (LinkedIn/Google) when the profile
      // has none yet, so it's pre-filled and one click from saved.
      setFullName(profile?.fullName || fullNameFromUser(user) || '')
      setHeadline(profile?.headline || '')
      setError(null)
    }
  }, [show, profile, user])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (show) window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [show, onClose])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!fullName.trim()) return
    setSaving(true)
    setError(null)
    try {
      await upsertProfile({ fullName: fullName.trim(), headline: headline.trim() || null })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setSaving(false)
    }
  }

  const { mounted, exiting, onAnimationEnd } = useAnimatePresence(show)

  if (!mounted) return null

  return (
    <div
      className={exiting ? 'acp-modal-backdrop-exit' : 'acp-modal-backdrop-enter'}
      onAnimationEnd={onAnimationEnd}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.5)',
        padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      data-testid="profile-modal"
    >
      <div className={exiting ? 'acp-modal-content-exit' : 'acp-modal-content-enter'}>
            <Box className="acp-card" p={8} w={{ base: '100%', md: '440px' }} maxW="440px">
              {/* Header */}
              <Flex align="center" gap={3} mb={6}>
                <Flex w="40px" h="40px" borderRadius="full" bg="acp.blue" color="white" align="center" justify="center">
                  <User size={20} />
                </Flex>
                <Text fontFamily="heading" fontSize="xl" fontWeight="bold" color="acp.ink">
                  {t('editProfile')}
                </Text>
              </Flex>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <Flex direction="column" gap={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={1} color="acp.ink">{t('fullName')}</Text>
                    <Input
                      className="acp-input"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder={t('fullNamePlaceholder')}
                      required
                      maxLength={150}
                    />
                  </Box>

                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={1} color="acp.ink">{t('headline')}</Text>
                    <Input
                      className="acp-input"
                      type="text"
                      value={headline}
                      onChange={(e) => setHeadline(e.target.value)}
                      placeholder={t('headlinePlaceholder')}
                      maxLength={200}
                    />
                  </Box>

                  {error && <Text fontSize="sm" color="red.500">{error}</Text>}

                  <Flex gap={3} mt={2}>
                    <Button
                      variant={"acp-ghost" as any}
                      size="sm"
                      onClick={onClose}
                      flex={1}
                      type="button"
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      variant={"acp-primary" as any}
                      size="sm"
                      type="submit"
                      flex={1}
                      disabled={saving || !fullName.trim()}
                    >
                      {saving ? t('saving') : t('save')}
                    </Button>
                  </Flex>
                </Flex>
              </form>
            </Box>
      </div>
    </div>
  )
}
